export type PhotoGroup = 'Waterfront' | 'Residences' | 'Resort & Amenities'

export interface ProjectPhoto {
  src: string
  title: string
  caption: string
  group: PhotoGroup
}

export const sevenIslands = {
  name: '7 Islands',
  subtitle: 'Resort & Marina Village',
  slug: '/projects/7-islands',
  location: 'Old Burnt Store Rd, Cape Coral, Florida',
  opening: 'March 2027',
  tagline:
    'A destination for waterfront living, vacationing, dining, shopping and entertainment.',
  communityIsland:
    'An entire island dedicated to a community center, a waterfront restaurant with outdoor seating, a shaded splash pad, kayak launch, outdoor amphitheater, dog park and a food truck park.',
  website: 'https://www.7islandsFL.com',
  email: 'info@7islandsFL.com',
  logo: 'https://cdn.magicpatterns.com/uploads/xcUvqt2sh6AxMqopjgrU55/7-islands-LOGO-VRL4w.png',
  heroImage:
    'https://cdn.magicpatterns.com/uploads/9hh8WmcEKpQEd7v76EaXnn/GGRMV_overview_from_waterway.jpg',
  masterPlan: {
    src: 'https://cdn.magicpatterns.com/uploads/3pxJfkHffMhEQRtRrxuVYr/7_Islands_2026-08-10_-_22-111_-_MASTER_CONCEPT_PLAN_(1).jpg',
    title: 'Master concept plan',
    caption:
      'Islands 1–7 with Frontage 1 and Frontage 2 along Old Burnt Store Road North.',
  },
  facts: [
    { label: 'Opening', value: 'March 2027' },
    { label: 'Site', value: '±47 acres' },
    { label: 'Islands', value: '7' },
    { label: 'Retail & commercial', value: '±37,000 sq ft' },
  ],
  amenities: [
    'Condominiums',
    'Apartments',
    'Custom homes with boat slips & rooftop terraces',
    'Autograph-level hotel',
    'Marina & boat slips',
    'Resort lagoon',
    'Waterfront restaurants',
    'Retail & commercial',
  ],
  islands: [
    { number: 1, name: 'Condominium A', detail: 'Waterfront condominium residences.' },
    { number: 2, name: 'Condominium B', detail: 'Waterfront condominium residences.' },
    { number: 3, name: 'Apartments A', detail: 'Rental residences on the canal.' },
    { number: 4, name: 'Apartments B', detail: 'Rental residences on the canal.' },
    { number: 5, name: 'Apartments C', detail: 'Rental residences on the canal.' },
    {
      number: 6,
      name: 'The Resort at Cape Coral',
      detail: 'Autograph-level hotel with an 84,900 sq ft white-sand resort lagoon.',
    },
    {
      number: 7,
      name: 'Community Center Park',
      detail: 'Event hall, splash pad, waterfront dining, kayak launch and amphitheater.',
    },
  ],
  frontage:
    'Along Old Burnt Store Road, the frontage holds custom homes with private boat slips and rooftop terraces, plus eight commercial buildings for shops and restaurants.',
}

// Every photo appears once. Low-resolution duplicates and the brochure
// collage (which repeats these renderings) are intentionally left out.
export const sevenIslandsPhotos: ProjectPhoto[] = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/3bZ9sVBuuL5wh1KdU8ftCB/GGRMV_fish_houses_along_old_burnt_store_road.jpg',
    title: 'Along Old Burnt Store Road',
    caption: 'Custom homes on the frontage with the condominium islands behind.',
    group: 'Waterfront',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/7BxY1zAm3zxrC5FACM3fAt/GGRMV_Resort_on_left_and_Island_7_to_the_right.jpg',
    title: 'The Resort and Island 7',
    caption: 'Resort lagoon on the left, the community island on the right.',
    group: 'Waterfront',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/cdLmomQJEhRUAwS7Nxvs7n/CCGG_cam004_Island_IV_e_(3).jpg',
    title: 'Island IV from the water',
    caption: 'Residences set back from a landscaped seawall and day docks.',
    group: 'Waterfront',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/2qTcGRnyYHda8Ce4LeN321/CCGG_cam003_Island_IV_e_(6).jpg',
    title: 'Marina canal',
    caption: 'Private boat slips steps from the residences on Island IV.',
    group: 'Waterfront',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/2zb3Ev1rZXZSk5FTem2zT2/CA_Backside_edit-1-1024x576.webp',
    title: 'Canal-side custom homes',
    caption: 'Every home opens onto its own dock and boat slip.',
    group: 'Residences',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/raiPhtTVcKSizNjTh7d8V7/CA_CloseUp_S_edit_(1).jpg',
    title: 'Street-side arrival',
    caption: 'Garages, landscaped entries and a walkable street front.',
    group: 'Residences',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/o9KUHPorPcx5BL9mSEokHo/CA_TopView_edit-1-819x1024.webp',
    title: 'Rooftop plan',
    caption: 'Private pool, lounge deck, outdoor kitchen and solar from above.',
    group: 'Residences',
  },
  {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/c3cbcaac-6421-438f-bc7b-31e9bf24bf67.jpg',
    title: 'Rooftop terrace',
    caption: 'Fire table, shaded pergola and plunge pool with views to the bay.',
    group: 'Residences',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/pnUbZH9PYtsGgXWSjNM1Cm/CCGG_cam005_Island_VI_e.jpg',
    title: 'The Resort at Cape Coral',
    caption: 'Autograph-level hotel arrival on Island 6.',
    group: 'Resort & Amenities',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/ohk8kuFs5nrLzQgznq9A4u/CCGG_cam007_Island_VI_e.jpg',
    title: 'Resort lagoon',
    caption: 'White-sand lagoon and cabanas with the hotel beyond.',
    group: 'Resort & Amenities',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/1BR5M8tNm6vUi2f7n7kRwL/CC_GRMV_Concep_Rend_7.jpg',
    title: 'Waterfront dining',
    caption: 'Outdoor seating on the canal at sunset.',
    group: 'Resort & Amenities',
  },
]

export const photoGroups: PhotoGroup[] = [
  'Waterfront',
  'Residences',
  'Resort & Amenities',
]
